import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { WfButton } from "@/components/site/primitives";
import { ORG, STATS, PHOTO_CREDIT } from "@/content/worldFarmers";
import { scrollToId } from "@/lib/scrollTo";

export const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -40]);

  const go = (id) => (e) => {
    e.preventDefault();
    scrollToId(id);
  };

  return (
    <section
      id="top"
      ref={ref}
      className="wf-grain relative overflow-hidden bg-[var(--wf-cream)]"
      aria-labelledby="hero-headline"
    >
      {/* Soft background wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(1200px_600px_at_18%_8%,rgba(233,223,201,0.6),transparent_55%),radial-gradient(900px_500px_at_88%_0%,rgba(201,211,191,0.5),transparent_60%)]"
      />
      <div className="wf-container relative grid grid-cols-1 items-center gap-10 py-14 sm:py-16 lg:grid-cols-12 lg:gap-8 lg:py-24">
        {/* Copy panel */}
        <div className="order-2 lg:order-1 lg:col-span-5">
          <motion.p
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--wf-maroon)]"
          >
            <span aria-hidden="true" className="h-px w-6 bg-[var(--wf-maroon)]/50" />
            Lancaster, Massachusetts · Since {ORG.founded}
          </motion.p>

          <motion.h1
            id="hero-headline"
            data-testid="hero-headline"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
            className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-[-0.01em] text-[var(--wf-ink)] sm:text-5xl lg:text-6xl"
          >
            Supporting farmers who feed our communities.
          </motion.h1>

          <div aria-hidden="true" className="mt-6 h-[3px] w-16 rounded-full bg-[var(--wf-maroon)]" />

          <motion.p
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
            className="mt-6 max-w-[52ch] text-base leading-7 text-[var(--wf-muted-ink)] text-muted-foreground sm:text-lg"
          >
            {ORG.missionShort}
          </motion.p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <WfButton as="a" href="#get-involved" onClick={go("get-involved")} withArrow data-testid="hero-primary-cta-button">
              Get Involved
            </WfButton>
            <WfButton as="a" href="#where-to-buy" onClick={go("where-to-buy")} variant="secondary" data-testid="hero-secondary-cta-button">
              Where to buy crops
            </WfButton>
          </div>

          {/* Stat row */}
          <dl className="mt-10 flex flex-wrap items-end gap-x-8 gap-y-4">
            {STATS.slice(0, 3).map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 && (
                  <span aria-hidden="true" className="hidden h-10 w-px self-center bg-[var(--wf-border)] sm:block" />
                )}
                <div data-testid={`hero-impact-stat-${s.id}`}>
                  <dt className="sr-only">{s.label}</dt>
                  <dd className="font-serif text-3xl font-semibold text-[var(--wf-maroon)] sm:text-4xl">
                    {s.value}
                  </dd>
                  <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                    {s.label}
                  </p>
                </div>
              </React.Fragment>
            ))}
          </dl>
        </div>

        {/* Image */}
        <div className="order-1 lg:order-2 lg:col-span-7">
          <motion.figure
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="group relative"
          >
            <div className="relative overflow-hidden rounded-sm border border-[var(--wf-border)] shadow-[var(--wf-shadow)]">
              <motion.img
                style={{ y }}
                src="/images/wf/farm-fabiola.jpg"
                alt="A woman in colorful traditional clothing harvesting green plants in an open field under a dramatic sky."
                className="aspect-[4/3] w-full scale-105 object-cover sm:aspect-[16/10]"
                loading="eager"
              />
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
              <figcaption className="absolute left-3 bottom-3">
                <a
                  href={PHOTO_CREDIT.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-[rgba(255,253,248,0.9)] px-3 py-1 text-[11px] font-medium text-[var(--wf-soil)] backdrop-blur-sm transition-opacity hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {PHOTO_CREDIT.label}
                </a>
              </figcaption>
            </div>
          </motion.figure>
        </div>
      </div>
    </section>
  );
};
